import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI } from '../../services/api';
import { Globe, Trophy, CheckCircle, XCircle, Info } from 'lucide-react';

const GlobalPoolPage = () => {
  const { isDarkMode } = useTheme();
  const [qualifications, setQualifications] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mlmAPI.getPoolQualifications()
      .then((res) => setQualifications(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  const pools = qualifications?.pools || [];
  const userQuals = qualifications?.user_qualifications || {};

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Global Partner Pool</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>9-tier pool structure with rank-based qualifications</p>
      </div>

      {qualifications?.user_summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={cardClass}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your Rank</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{qualifications.user_summary.current_rank || '—'}</p>
          </div>
          <div className={cardClass}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Qualified Pools</p>
            <p className={`text-2xl font-bold text-green-500`}>{qualifications.user_summary.qualified_pools || 0} / {pools.length}</p>
          </div>
          <div className={cardClass}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Pool Earnings</p>
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              ${qualifications.user_summary.total_pool_earnings?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      )}

      <div className={cardClass}>
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-orange-500" />
          <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Pool Structure & Qualifications</h3>
        </div>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          The Global Partner Pool distributes 3% of company revenue across 9 pools. Each pool has specific rank requirements.
          Partners who meet the rank criteria for a pool are automatically qualified to share in that pool's distribution.
        </p>

        {pools.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pool</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rank Range</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Qualified</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your Status</th>
                </tr>
              </thead>
              <tbody>
                {pools.map((pool, i) => {
                  const qualified = userQuals[pool.id]?.qualified || userQuals[pool.name]?.qualified;
                  return (
                    <tr key={pool.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                      <td className={`py-3 px-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-orange-500" />
                          {pool.name || `Pool ${i + 1}`}
                        </div>
                      </td>
                      <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {pool.min_rank || '—'} → {pool.max_rank || '—'}
                      </td>
                      <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {pool.qualified_count || 0} partners
                      </td>
                      <td className="py-3 px-4">
                        {qualified ? (
                          <span className="inline-flex items-center gap-1 text-green-500">
                            <CheckCircle className="w-4 h-4" /> Qualified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-400">
                            <XCircle className="w-4 h-4" /> Not Qualified
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No pool data available</p>
        )}
      </div>
    </motion.div>
  );
};

export default GlobalPoolPage;
